import type { ReactNode } from 'react'
import type { CSSVars } from '@/types/css'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { markAdapted } from '@/lib/adaptation'
import { loadStars } from '@/lib/astro/catalogue'
import { eqjToHorizon } from '@/lib/astro/sky'
import { prefersReducedMotion } from '@/lib/preferences'
import { CosmicLensGlyph } from '@/components/generals/wordmark'
import styles from '@/components/generals/dark-adaptation.module.css'

type WarpStar = {
    x: number
    y: number
    z: number
    warmth: number
    size: number
}

const SESSION_KEY = 'aphelion:adapted'
const ESTIMATED_COUNT = 2400
const NAME = 'CosmicLens'
/** How long the mark takes to assemble, then a short beat on the finished mark before the aperture opens. */
const BUILD_MS = 1950
const HOLD_MS = 320
/** How long the aperture takes to open onto the page. */
const OPEN_MS = 950
/** Scripts that arrive after this have missed the intro: the CSS fallback is already lifting it. */
const LATE_MS = 4000
/** The arrival: the stars rush past at this speed and slow to a drift over the first second. */
const ARRIVAL_SPEED = 0.05
const DRIFT_SPEED = 0.0016
const ARRIVAL_MS = 1100

const clamp01 = (value: number): number => (value < 0 ? 0 : value > 1 ? 1 : value)

const easeOut = (t: number): number => 1 - Math.pow(1 - t, 3)

const spawn = (star: WarpStar, deep: boolean) => {
    star.x = (Math.random() * 2 - 1) * 1.6
    star.y = (Math.random() * 2 - 1) * 1.1
    star.z = deep ? 0.85 + Math.random() * 0.15 : 0.04 + Math.random() * 0.96
    star.warmth = Math.random()
    star.size = 0.5 + Math.pow(Math.random(), 4) * 1.6
}

/**
 * The site opens on black: the sky arrives at warp speed and settles into a slow drift while the CosmicLens
 * mark assembles itself in the middle, then the aperture opens onto the page and the stars stream past again.
 */
export const DarkAdaptation = (): ReactNode => {
    const [phase, setPhase] = useState<'showing' | 'opening' | 'gone'>('showing')
    const rootRef = useRef<HTMLDivElement | null>(null)
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const countRef = useRef<HTMLSpanElement | null>(null)
    const finishRef = useRef<() => void>(() => undefined)

    useEffect(() => {
        const html = document.documentElement
        const root = rootRef.current
        const canvas = canvasRef.current
        const context = canvas?.getContext('2d')
        // Scripts arrived after the CSS fallback began lifting the overlay: never bring the black back over a page already in view.
        const late = performance.now() > LATE_MS

        if (html.classList.contains('adapted') || late || !root || !canvas || !context) {
            if (late) {
                html.classList.add('adapted')

                try {
                    sessionStorage.setItem(SESSION_KEY, '1')
                }
                catch {
                    // Storage can be unavailable; nothing else depends on it.
                }
            }

            markAdapted()
            setPhase('gone')
            return
        }

        root.classList.add('is-controlled')

        const reduced = prefersReducedMotion() || html.getAttribute('data-motion') === 'reduced'
        const build = reduced ? 600 : BUILD_MS
        const previousOverflow = document.body.style.overflow
        const ratio = Math.min(window.devicePixelRatio || 1, 2)
        const width = canvas.clientWidth
        const height = canvas.clientHeight
        const focal = Math.min(width, height) * 0.55
        const stars: WarpStar[] = Array.from({ length: reduced ? 260 : width < 640 ? 260 : 420 }, () => {
            const star = { x: 0, y: 0, z: 1, warmth: 0, size: 1 }

            spawn(star, false)

            return star
        })
        let target = ESTIMATED_COUNT
        let frame = 0
        let finished = false
        let openedAt = -1
        let liftTimer = 0
        let goneTimer = 0
        let last = 0

        canvas.width = Math.round(width * ratio)
        canvas.height = Math.round(height * ratio)
        context.setTransform(ratio, 0, 0, ratio, 0, 0)
        document.body.style.overflow = 'hidden'

        loadStars()
            .then(catalogue => {
                const m = eqjToHorizon(new Date())
                let visible = 0

                for (let i = 0; i < catalogue.count; i++) {
                    const x = catalogue.xyz[i * 3]
                    const y = catalogue.xyz[i * 3 + 1]
                    const z = catalogue.xyz[i * 3 + 2]

                    if (m[6] * x + m[7] * y + m[8] * z > 0) visible++
                }

                target = visible
            })
            .catch(() => undefined)

        const finish = () => {
            if (finished) return
            finished = true
            window.clearTimeout(liftTimer)
            openedAt = performance.now()

            try {
                sessionStorage.setItem(SESSION_KEY, '1')
            }
            catch {
                // Storage can be unavailable; the intro will simply show again next time.
            }

            if (countRef.current) countRef.current.textContent = target.toLocaleString('en-GB')

            markAdapted()
            setPhase('opening')
            document.body.style.overflow = previousOverflow
            goneTimer = window.setTimeout(() => {
                cancelAnimationFrame(frame)
                html.classList.add('adapted')
                setPhase('gone')
            }, reduced ? 650 : OPEN_MS)
        }

        finishRef.current = finish

        const start = performance.now()

        const draw = (now: number) => {
            const elapsed = now - start
            const step = last ? Math.min(3, (now - last) / 16.67) : 1

            last = now

            // Rushing in, slowing to a drift, then streaming past again as the aperture opens.
            let speed = reduced ? 0 : ARRIVAL_SPEED * Math.pow(1 - clamp01(elapsed / ARRIVAL_MS), 2.2) + DRIFT_SPEED

            if (openedAt >= 0 && !reduced) speed += Math.pow(clamp01((now - openedAt) / OPEN_MS), 2) * 0.06

            const appear = clamp01(elapsed / 250)
            const cx = width / 2
            const cy = height / 2

            context.globalCompositeOperation = 'source-over'
            context.fillStyle = 'rgba(0, 0, 0, 0.55)'
            context.fillRect(0, 0, width, height)
            context.globalCompositeOperation = 'lighter'
            context.lineCap = 'round'

            for (const star of stars) {
                const previousZ = star.z

                star.z -= speed * step

                if (star.z < 0.02) {
                    spawn(star, true)
                    continue
                }

                const x = cx + (star.x / star.z) * focal
                const y = cy + (star.y / star.z) * focal

                if (x < -40 || y < -40 || x > width + 40 || y > height + 40) {
                    spawn(star, true)
                    continue
                }

                const px = cx + (star.x / previousZ) * focal
                const py = cy + (star.y / previousZ) * focal
                const near = 1 - star.z
                const alpha = Math.min(1, 0.15 + near * 1.1) * appear
                const red = 230 + Math.round(star.warmth * 25)
                const blue = 205 + Math.round((1 - star.warmth) * 50)

                context.strokeStyle = `rgba(${red}, 228, ${blue}, ${alpha})`
                context.lineWidth = Math.max(0.6, star.size * (0.4 + near * 1.6))
                context.beginPath()
                context.moveTo(px, py)
                context.lineTo(x + 0.01, y)
                context.stroke()
            }

            const progress = clamp01(elapsed / build)

            if (countRef.current && !finished) countRef.current.textContent = Math.round(target * easeOut(progress)).toLocaleString('en-GB')

            if (!finished && elapsed >= build && !liftTimer) liftTimer = window.setTimeout(finish, reduced ? 120 : HOLD_MS)

            // Reduced motion draws one still frame; otherwise the field keeps moving until the overlay is gone.
            if (!reduced) frame = requestAnimationFrame(draw)
            else if (!finished && elapsed < build) frame = requestAnimationFrame(draw)
        }

        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Tab') return
            finish()
        }

        frame = requestAnimationFrame(draw)
        window.addEventListener('keydown', handleKey)

        return () => {
            cancelAnimationFrame(frame)
            window.clearTimeout(liftTimer)
            window.clearTimeout(goneTimer)
            window.removeEventListener('keydown', handleKey)
            document.body.style.overflow = previousOverflow
        }
    }, [])

    if (phase === 'gone') return null

    return (
        <div
            ref={rootRef}
            className={cn('dark-adaptation fixed inset-0 z-[95] bg-black', phase === 'opening' && 'is-opening pointer-events-none')}
        >
            <div className='media absolute inset-0'>
                <canvas ref={canvasRef} className='block h-full w-full' />
            </div>

            <span aria-hidden className={styles.barrel} />

            <div className={cn('absolute inset-0 flex flex-col items-center justify-center px-6 text-center', styles.stage)}>
                <CosmicLensGlyph
                    className={cn('h-auto w-[min(58vw,17rem)]', styles.mark)}
                    parts={{ ring: styles.ring, crescent: styles.crescent, orbit: styles.orbit, trails: styles.trails, star: styles.star }}
                >
                    <circle cx='612' cy='230' r='46' fill='none' stroke='var(--c-brass)' strokeWidth='3' className={styles.flare} />
                    <g stroke='var(--c-star)' strokeWidth='3' strokeLinecap='round' className={styles.rays}>
                        {Array.from({ length: 8 }, (_, index) => {
                            const angle = (index * Math.PI) / 4
                            const inner = index % 2 === 0 ? 62 : 58
                            const outer = index % 2 === 0 ? 92 : 76

                            return (
                                <line
                                    key={index}
                                    x1={(612 + Math.cos(angle) * inner).toFixed(1)}
                                    y1={(230 + Math.sin(angle) * inner).toFixed(1)}
                                    x2={(612 + Math.cos(angle) * outer).toFixed(1)}
                                    y2={(230 + Math.sin(angle) * outer).toFixed(1)}
                                />
                            )
                        })}
                    </g>
                </CosmicLensGlyph>

                <p className='mt-8 font-brand text-[2.6rem] leading-none tracking-[0.01em] text-star md:text-[3.4rem]'>
                    <span className='sr-only'>
                        {NAME}
                    </span>
                    {Array.from(NAME).map((character, index) => {
                        const style: CSSVars = { '--i': index }

                        return (
                            <span key={index} aria-hidden className={styles.letter} style={style}>
                                {character}
                            </span>
                        )
                    })}
                </p>
                <p className={cn('label-mono mt-5 text-star-3', styles.status)}>
                    <span ref={countRef} className='tabular-nums text-star-2'>
                        0
                    </span>
                    {' stars above the horizon tonight'}
                </p>
            </div>

            <button
                type='button'
                onClick={() => finishRef.current()}
                className='label-mono absolute bottom-6 right-6 rounded-full px-3 py-2 text-star-3 transition-colors hover:text-star'
            >
                Skip
            </button>
        </div>
    )
}
